/** 神策 WebPopup 下发 AI 版下载回访问卷时使用的业务类型。 */
export const AI_DOWNLOAD_FOLLOWUP_SURVEY_POPUP_TYPE =
  'ai_download_followup_survey'

/** 神策圈选用户进入 Web 后下发问卷资格时使用的页面事件名。 */
export const AI_DOWNLOAD_FOLLOWUP_SURVEY_ELIGIBLE_EVENT =
  'ai-download-followup-survey-eligible'

/** 下载回访问卷支持的用户标识类型。 */
export type AiDownloadFollowupSurveyUserId = string | number | null | undefined

type AiDownloadFollowupSurveyStatus = 'pending' | 'dismissed' | 'submitted'

interface AiDownloadFollowupSurveyRecord {
  status: AiDownloadFollowupSurveyStatus
  updatedAt: number
}

/** 神策圈选事件携带的账号信息，供“全部照片”页判断是否打开问卷。 */
export interface AiDownloadFollowupSurveyEligibleDetail {
  /** 被神策历史下载事件圈中的用户 ID。 */
  userId: string
}

const STORAGE_KEY_PREFIX = 'ai_download_followup_survey_v1'

function normalizeUserId(userId: AiDownloadFollowupSurveyUserId): string {
  return String(userId ?? '').trim()
}

function buildStorageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}_${userId}`
}

function readRecord(userId: string): AiDownloadFollowupSurveyRecord | null {
  try {
    const storedValue = window.localStorage.getItem(buildStorageKey(userId))
    if (!storedValue) return null

    const parsedValue = JSON.parse(
      storedValue
    ) as Partial<AiDownloadFollowupSurveyRecord>
    if (
      parsedValue.status !== 'pending' &&
      parsedValue.status !== 'dismissed' &&
      parsedValue.status !== 'submitted'
    ) {
      return null
    }

    return {
      status: parsedValue.status,
      updatedAt:
        typeof parsedValue.updatedAt === 'number' ? parsedValue.updatedAt : 0
    }
  } catch {
    return null
  }
}

function writeRecord(
  userId: string,
  status: AiDownloadFollowupSurveyStatus
): void {
  try {
    const record: AiDownloadFollowupSurveyRecord = {
      status,
      updatedAt: Date.now()
    }
    window.localStorage.setItem(buildStorageKey(userId), JSON.stringify(record))
  } catch {
    // 存储不可用时仍允许本次事件打开问卷，仅无法跨刷新保留状态。
  }
}

/**
 * 记录当前账号点击过 Web 端 AI 版下载，但不在点击时打开问卷。
 * “全部照片”页在后续重新进入或刷新时读取该状态，作为神策圈选实时性不足时的补充。
 *
 * @param userId - 当前登录用户 ID
 * @returns 是否首次记录当前问卷版本的下载行为
 *
 * @example
 * ```ts
 * recordAiDownloadFollowupSurveyDownload('xuser-123')
 * ```
 */
export function recordAiDownloadFollowupSurveyDownload(
  userId: AiDownloadFollowupSurveyUserId
): boolean {
  const normalizedUserId = normalizeUserId(userId)
  if (!normalizedUserId || typeof window === 'undefined') return false
  if (readRecord(normalizedUserId)) return false

  writeRecord(normalizedUserId, 'pending')
  return true
}

/**
 * 将神策圈选的当前账号标记为待填写，并通知“全部照片”页打开问卷。
 * 当前问卷版本已经展示、关闭或提交过的账号不会再次进入待填写状态。
 *
 * @param userId - 当前登录用户 ID
 * @returns 是否接受本次资格并通知页面展示
 *
 * @example
 * ```ts
 * markAiDownloadFollowupSurveyEligible('xuser-123')
 * ```
 */
export function markAiDownloadFollowupSurveyEligible(
  userId: AiDownloadFollowupSurveyUserId
): boolean {
  const normalizedUserId = normalizeUserId(userId)
  if (!normalizedUserId || typeof window === 'undefined') return false
  const existingRecord = readRecord(normalizedUserId)
  if (
    existingRecord?.status === 'dismissed' ||
    existingRecord?.status === 'submitted'
  ) {
    return false
  }

  if (!existingRecord) writeRecord(normalizedUserId, 'pending')
  window.dispatchEvent(
    new CustomEvent<AiDownloadFollowupSurveyEligibleDetail>(
      AI_DOWNLOAD_FOLLOWUP_SURVEY_ELIGIBLE_EVENT,
      { detail: { userId: normalizedUserId } }
    )
  )
  return true
}

/**
 * 判断当前账号是否有一次待展示的下载回访问卷。
 *
 * @param userId - 当前登录用户 ID
 * @returns 状态为待填写时返回 true
 *
 * @example
 * ```ts
 * shouldShowAiDownloadFollowupSurvey('xuser-123')
 * ```
 */
export function shouldShowAiDownloadFollowupSurvey(
  userId: AiDownloadFollowupSurveyUserId
): boolean {
  const normalizedUserId = normalizeUserId(userId)
  if (!normalizedUserId || typeof window === 'undefined') return false
  return readRecord(normalizedUserId)?.status === 'pending'
}

/**
 * 记录用户主动关闭问卷，当前问卷版本不再重复展示。
 *
 * @param userId - 当前登录用户 ID
 * @returns 无返回值
 *
 * @example
 * ```ts
 * dismissAiDownloadFollowupSurvey('xuser-123')
 * ```
 */
export function dismissAiDownloadFollowupSurvey(
  userId: AiDownloadFollowupSurveyUserId
): void {
  const normalizedUserId = normalizeUserId(userId)
  if (!normalizedUserId || typeof window === 'undefined') return
  if (readRecord(normalizedUserId)?.status === 'submitted') return
  writeRecord(normalizedUserId, 'dismissed')
}

/**
 * 记录当前账号已经提交问卷，后续下载不再重复弹出。
 *
 * @param userId - 当前登录用户 ID
 * @returns 无返回值
 *
 * @example
 * ```ts
 * submitAiDownloadFollowupSurvey('xuser-123')
 * ```
 */
export function submitAiDownloadFollowupSurvey(
  userId: AiDownloadFollowupSurveyUserId
): void {
  const normalizedUserId = normalizeUserId(userId)
  if (!normalizedUserId || typeof window === 'undefined') return
  writeRecord(normalizedUserId, 'submitted')
}
